import { useState, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Tooltip from "@mui/material/Tooltip";

import SuiBadgeDot from "components/SuiBadgeDot";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";
import SuiAvatar from "components/SuiAvatar";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import toast from "react-hot-toast";

import DataTable from "examples/Tables/DataTable";
import { Table } from "antd";

import Actionables from "components/UpStyled/Actionables";
import NumberFormat from "react-number-format";
import ReadMore from "components/UpStyled/ReadMore";

import api from "services/api";
import moment from "moment";

import { projectSortOptions, projectStatusOptions, budgetOptions } from "utils/data";
import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";
import curved9 from "assets/images/upstyled/default_background.png";

/**
 * require more informations when user create a new style
 */
const RequestDetailModal = ({ visible, handleClose, handleOk, detail = {} }) => {
  const navigate = useNavigate();

  const manufacturer = detail?.manufacturer || {};
  const store =
    manufacturer?.stores && manufacturer?.stores.length > 0 ? manufacturer.stores[0] : {};

  const formatAccountStatus = (type, row) => {
    const option = projectStatusOptions.find((x) => x.value == type);
    if (option) {
      if (option.value == "init") {
        return <SuiBadgeDot color="warning" badgeContent={option.label} />;
      } else if (option.value == "requested") {
        return <SuiBadgeDot size="xs" color="info" badgeContent={option.label} />;
      } else if (option.value == "active") {
        return <SuiBadgeDot size="xs" color="success" badgeContent={option.label} />;
      } else if (option.value == "canceled") {
        return <SuiBadgeDot color="error" badgeContent={option.label} />;
      } else if (option.value == "completed") {
        return <SuiBadgeDot color="dark" badgeContent={option.label} />;
      } else if (row?.deleted_at) {
        return (
          <Tooltip
            title={`Deleted at ${moment(row.deleted_at).format("YYYY-MM-DD")}`}
            placement="top"
          >
            <SuiBadgeDot color="error" badgeContent={"Deleted"} />
          </Tooltip>
        );
      } else {
        return <SuiBadgeDot color="secondary" badgeContent={option.label} />;
      }
    } else {
      return type;
    }
  };

  const getProjectTableRow = (item) => {
    const quote = item.quote ? item.quote : {};
    const products = item.products || [];
    let styleImages = [];
    //add style first image
    for (let i = 0; i < products.length; i++) {
      let temp = getStyleImages(products[i], false);
      styleImages.push(...temp);
    }
    //add all project reference image
    const fileList = item?.metadata?.othersFiles || [];
    const files = fileList.filter((f) => f?.type && f.type.includes("image"));
    if (files && files.length > 0) {
      styleImages.push(...files);
    }
    if (styleImages.length == 0) {
      styleImages = [{ url: curved9, name: "deafult_image" }];
    }

    const ownerStore =
      item?.owner?.stores && item.owner.stores.length > 0 ? item.owner.stores[0] : {};
    const ownerName = item?.store_name ? item?.store_name : ownerStore?.name;
    const maId = detail?.manufacturer?.id;
    const allResponse = item?.response || {};

    const designerCom = (
      <SuiBox
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        gap={1}
        sx={{ maxWidth: 260 }}
      >
        <SuiBox>
          <Tooltip position="top" title={ownerName}>
            <SuiAvatar
              src={ownerStore?.metadata?.avatar}
              alt="profile-image"
              variant="rounded"
              size="sm"
              shadow="sm"
            />
          </Tooltip>
        </SuiBox>
        <SuiBox>
          <SuiTypography variant="button" fontWeight="regular" textTransform="capitalize">
            {ownerName}
          </SuiTypography>
        </SuiBox>
      </SuiBox>
    );

    return {
      key: item.id,
      image: styleImages[0],
      name: item.name,
      designerCom: designerCom,
      budget: quote.budget,
      budget_min: quote.budget_min,
      budget_max: quote.budget_max,
      desired_end_date: quote.desired_end_date,
      desired_start_date: quote.desired_end_date,
      created_at: item.created_at,
      deleted_at: item.deleted_at,
      status: item.status,
      response: allResponse[maId],
    };
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      width: 60,
      render: (text, record) => {
        return (
          <Link to={`/job/requests?id=${record?.key}`}>
            <SuiBox
              src={
                text.url
                  ? canResizeImageUrl(text.type)
                    ? text.url + "?x-oss-process=image/resize,m_fill,h_60,w_60"
                    : text.url
                  : text.preview
              }
              component="img"
              sx={{
                overflow: "hidden",
                width: "auto",
                height: 60,
                width: 60,
                // boxShadow: ({ boxShadows: { md } }) => md,
                objectFit: "contain",
                objectPosition: "center",
              }}
            />
          </Link>
        );
      },
    },
    {
      title: "Project Name",
      dataIndex: "name",
    },
    {
      title: "Owner(Designer)",
      dataIndex: "designerCom",
    },
    {
      title: "status",
      dataIndex: "status",
      width: 60,
      render: (text, record) => formatAccountStatus(text, record),
    },
    {
      title: "Desired Start Date",
      dataIndex: "desired_start_date",
      width: 120,
      render: (text) => {
        return <div>{moment(text).format("YYYY-MM-DD")}</div>;
      },
    },
    {
      title: "Response",
      dataIndex: "response",
      width: 400,
      render: (text) => {
        return (
          <SuiTypography variant="body2" color="secondary">
            {text?.date ? moment(text?.date).format("YYYY-MM-DD HH:mm:ss") : ""}
            <ReadMore limit={180} variant="body2" textTransform="none" color="text">
              {text?.message}
            </ReadMore>
          </SuiTypography>
        );
      },
    },
  ];
  const rows = detail.projectList.map((item) => getProjectTableRow(item));

  const handleRowClick = (row) => {
    let link = `/a/projects?id=${row?.key}`;

    navigate(link);
    //window.open(`/workspace/styles-master?id=${row?.original?.key}`, "_blank");
  };

  return (
    <Dialog
      open={visible}
      onClose={(e, reason) => {
        if (reason != "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="lg"
    >
      <DialogTitle id="alert-dialog-title">
        Requests received by {store.name}
        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Table
          columns={columns}
          dataSource={rows}
          onRow={(record) => {
            return {
              onClick: (event) => handleRowClick(record),
            };
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default RequestDetailModal;
